// One asset class other than stocks, at its own path (/stablecoins,
// /treasuries, /govt-debt, /credit, /money-market, /commodities, /private-equity, /real-estate): the terminal chrome with the asset-class
// navigation lit on this class, then the class's screen. Stablecoins is the
// U-4/U-10 screen; the other seven are class boards. On a class board a
// supply-valued row opens the token panel (U-3), with the same ?token= deep
// link the stock screens use.

import { useCallback, useEffect, useState } from "react";

import { API_BASE } from "../api";
import { classById, isBoardClass, type AssetClassId } from "../classes";
import { ClassBoard } from "../components/ClassBoard";
import { ClassSwitch } from "../components/ClassSwitch";
import { StablecoinsScreen } from "../components/Stablecoins";
import { TokenPanel } from "../components/TokenPanel";
import { parseTokenId } from "../token-api";

export type ClassPageId = Exclude<AssetClassId, "stocks">;

function readToken(): string | null {
  return parseTokenId(new URLSearchParams(location.search).get("token"))?.id ?? null;
}

function writeToken(id: string | null): void {
  const url = new URL(location.href);
  if (id === null) url.searchParams.delete("token");
  else url.searchParams.set("token", id);
  history.replaceState(null, "", url);
}

export function ClassPage({ classId }: { classId: ClassPageId }) {
  const cls = classById(classId);
  const board = isBoardClass(classId);
  const [selectedToken, setSelectedToken] = useState<string | null>(() => (board ? readToken() : null));

  useEffect(() => {
    const previous = document.title;
    document.title = `${cls.label} — Panther Terminal`;
    return () => {
      document.title = previous;
    };
  }, [cls.label]);

  const openToken = useCallback((id: string) => {
    setSelectedToken(id);
    writeToken(id);
  }, []);
  const closeToken = useCallback(() => {
    setSelectedToken(null);
    writeToken(null);
  }, []);

  return (
    <div className="terminal">
      <p className="command-bar">
        <span aria-hidden className="cursor" />
        <span>
          {cls.code} &lt;{cls.sector}&gt; <em>{cls.title}</em> &lt;GO&gt;
        </span>
        <span>PANTHER/TERMINAL</span>
      </p>
      <main>
        <header className="screen-head">
          <div className="screen-title">
            <h1 className="sr-only">{cls.label}</h1>
            <ClassSwitch current={classId} />
          </div>
          <span className="head-links">
            <a className="head-link" href="/tokenization/methodology">
              How we count
            </a>
            <a className="head-link" href={`${API_BASE}/v1`} rel="noreferrer" target="_blank">
              API
            </a>
          </span>
        </header>

        {isBoardClass(classId) ? <ClassBoard classId={classId} onSelectToken={openToken} /> : <StablecoinsScreen />}
      </main>
      <footer className="footer">
        {board
          ? "Panther Research · Data from issuer NAV feeds, on-chain reads and DefiLlama, refreshed every 30 minutes. Not investment advice."
          : "Panther Research · Stablecoin data from on-chain reads and DefiLlama, refreshed every 30 minutes. Not investment advice."}
      </footer>
      {board ? <TokenPanel classId={classId} onClose={closeToken} onOpen={openToken} tokenId={selectedToken} /> : null}
    </div>
  );
}
